import { LucideIcon, Check } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FadeIn from "@/components/FadeIn";
import SEOHead from "@/components/SEOHead";
import { useQuoteModal } from "@/hooks/useQuoteModal";
import { industryBreadcrumb } from "@/lib/schema";

interface IndustryPageProps {
  seoTitle: string;
  seoDesc: string;
  canonical: string;
  h1: string;
  subtitle: string;
  industry: string;
  problem: string;
  solutions: { title: string; desc?: string }[];
  statBanner?: string;
  callout?: string;
  testimonial?: { quote: string; name: string; company: string };
  faqs: { q: string; a: string }[];
  relatedServices?: { title: string; href: string }[];
}

const IndustryPageTemplate = ({
  seoTitle, seoDesc, canonical, h1, subtitle, industry,
  problem, solutions, statBanner, callout, testimonial, faqs, relatedServices,
}: IndustryPageProps) => {
  const { open } = useQuoteModal();

  return (
    <>
      <SEOHead title={seoTitle} description={seoDesc} canonical={canonical}>
        <script type="application/ld+json">{JSON.stringify(industryBreadcrumb(industry, canonical))}</script>
        {faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}</script>
        )}
      </SEOHead>

      {/* Hero */}
      <section className="bg-navy pt-32 pb-20">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <FadeIn>
            <h1 className="secondary-page-title text-primary-foreground mb-6 whitespace-pre-line">{h1}</h1>
            <p className="text-lg text-primary-foreground/70 mb-8">{subtitle}</p>
            <Button className="h-auto min-h-12 max-w-full whitespace-normal bg-orange px-6 py-3 text-center text-base font-semibold text-accent-foreground hover:bg-orange/90 sm:px-8" onClick={() => open({ ctaSource: `industry_${industry.toLowerCase().replace(/[^a-z0-9]+/g, "_")}` })}>
              Get a {industry} Freight Quote
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* The Problem */}
      <section className="bg-dark py-16">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <FadeIn>
            <p className="text-primary-foreground/70 text-lg leading-relaxed">{problem}</p>
          </FadeIn>
        </div>
      </section>

      {callout && (
        <section className="bg-navy py-4 text-center">
          <p className="text-primary-foreground/80 text-sm font-medium max-w-3xl mx-auto px-4">{callout}</p>
        </section>
      )}

      {/* Solutions */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <FadeIn>
            <h2 className="section-title text-navy mb-12">How we solve it</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {solutions.map((s, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="border border-border rounded-xl p-6 h-full">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-orange flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold leading-snug text-navy mb-1">{s.title}</h3>
                      {s.desc && <p className="text-text-muted text-sm">{s.desc}</p>}
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Stat Banner */}
      {statBanner && (
        <section className="bg-orange py-10">
          <div className="container mx-auto px-4 text-center">
            <p className="text-accent-foreground text-xl lg:text-2xl font-bold">{statBanner}</p>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {testimonial && (
        <section className="bg-surface py-16">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <FadeIn>
              <p className="text-lg italic text-text-muted mb-4">"{testimonial.quote}"</p>
              <p className="font-semibold text-navy">{testimonial.name}</p>
              <p className="text-sm text-text-muted">{testimonial.company}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <FadeIn>
            <h2 className="section-title text-navy mb-12">Frequently Asked Questions</h2>
          </FadeIn>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-6">
                <AccordionTrigger className="text-left font-semibold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-text-muted">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-24">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <FadeIn>
            <h2 className="section-title text-primary-foreground mb-4">
              Talk to a {industry} Freight Specialist
            </h2>
            <p className="text-primary-foreground/60 mb-8">No pressure. Just honest answers about your freight challenges.</p>
            <Button className="h-auto min-h-12 max-w-full whitespace-normal bg-orange px-6 py-3 text-center text-base font-semibold text-accent-foreground hover:bg-orange/90 sm:px-8" onClick={() => open({ ctaSource: `industry_${industry.toLowerCase().replace(/[^a-z0-9]+/g, "_")}` })}>
              Get a Free Quote
            </Button>
          </FadeIn>
        </div>
      </section>
    </>
  );
};

export default IndustryPageTemplate;
